/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    getCatalogId,
    IComponent,
    IComponentProps,
    IGridSettings,
    IImageSettings,
    msdyn365Commerce,
    TelemetryEvent,
    getUrlSync
} from '@msdyn365-commerce/core';
import { ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import {
    ProductAvailableQuantity,
    ProductDimension,
    ProductPrice,
    ProductsDataActions,
    ProductSearchCriteria,
    ProductSearchResult,
    ProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, generateProductImageUrl, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, IPopupProps, ITelemetryContent, Popup } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useState, useMemo } from 'react';
import { IItemsAddedToCartResources, ItemsAddedToCartDialogComponent, OrderDetailsProduct } from '@msdyn365-commerce/components';
import { IBuyboxCommonResources } from './buyboxInterface';
import PQueue from 'p-queue';
import { humanizeUnitOfMeasure } from '../../../components/unit-of-measure';
import PriceComponent from '../../../themes/dlvry-adventureworks/views/components/price.component';
import { ProductSearchResultExt } from '../../../shared-utils/types';
import { BuyValidationResult } from '../../../themes/dlvry-adventureworks/views/components/product.component';
import { CartHelper } from '../../../shared-utils/cart-helper';
import { LifecycleStatus } from '../../../shared-utils/lifecycle-status';

/**
 * Interface for add to cart resources.
 */
export interface IAddToCartResources {
    goToCartText: string;
    continueShoppingText: string;
    closeNotificationLabel: string;
    headerItemOneText: string;
    headerItemFormatText: string;
    headerLinesFormatText?: string;
    headerMessageText: string;
    freePriceText: string;
    originalPriceText: string;
    currentPriceText: string;
    addedQuantityText: string;
    buyAgainNotificationTitle?: string;
    buyAgainNotificationCloseAriaLabel?: string;
    validationErrorMessage?: string;
    oneErrorText?: string;
    multiErrorsText?: string;
}

export interface IAddToCartToastResources {
    errorHeader?: string;
    productsAddedToCart?: string;
    productsRemovedFromCart?: string;
    productsUpdatedInCart?: string;
    goToCart?: string;
    noAccessToProduct?: string;
}

/**
 * Interface for add to cart components props.
 */
export interface IAddToCartComponentProps extends IComponentProps<{ cart?: ICartState, product: SimpleProduct; price?: ProductPrice; productSearch?: ProductSearchResult | undefined } | undefined> {
    mode?: 'default' | 'cart';
    className?: string;
    addToCartText: string;
    updateCartText: string;
    removeFromCartText: string;
    outOfStockText?: string;
    expiredText?: string;
    outOfSeasonBuyButtonText?: string;
    disabled?: boolean;
    quantity?: number;
    breakoff?: boolean;
    navigationUrl?: string;
    productAvailability?: ProductAvailableQuantity;
    getSelectedProduct?: Promise<SimpleProduct | null>;
    isNavigationToCartPageDisabled?: boolean;
    shouldSkipSiteSettings?: boolean;
    addToCartArialLabel?: string;
    products?: {
        productId: number;
        quantity: number;
        catalogId?: number;
    }[];
    cartQueue?: PQueue;
    orderDetailsProducts?: OrderDetailsProduct[];
    orderDetailsActivePrices?: ProductPrice[];
    hasAvailableProducts?: boolean;
    imageSettings?: IImageSettings;
    gridSettings?: IGridSettings;

    isLoading?: boolean;
    isUpdatingDimension?: boolean;
    isLoadingDeliveryOptions?: boolean;
    isUpdatingDeliveryOptions?: boolean;
    isAddServiceItemToCart?: boolean;
    isAddEmailDeliveryItemToCart?: boolean;
    isPriceKeyedIn?: boolean;
    customPriceAmount?: number;
    isOrderQuantityLimitsFeatureEnabled?: boolean;
    userHasAccess?: boolean;

    dialogStrings?: IAddToCartResources;
    toastStrings?: IAddToCartToastResources;
    buyBoxResources?: IBuyboxCommonResources;

    telemetryContent?: ITelemetryContent;

    catalogId?: number;

    isCustomPriceSelected?: boolean;
    maximumKeyInPrice?: number;
    minimumKeyInPrice?: number;
    defaultMaximumKeyInPrice?: number;
    defaultMinimumKeyInPrice?: number;
    isProductQuantityLoading?: boolean;

    onAdd?(result: ICartActionResult): void;
    onError?(result: IAddToCartFailureResult): void;
    changeUpdatingDimension?(isUpdatingDimension: boolean): void;
    changeUpdatingDeliveryOptions?(isUpdatingDeliveryOptions: boolean): void;
    onUpdateQuantity?(quantity: number): void;
    onBuyValidate?: (product: ProductSearchResultExt, quantity: number) => Promise<BuyValidationResult>;
    nonBreakoffUOM?: string;
}

/**
 * Cart action Failure reason type.
 */
export declare type ICartActionFailureReason =
    | 'EMPTYINPUT'
    | 'MISSINGDIMENSION'
    | 'OUTOFSTOCK'
    | 'CARTACTIONFAILED'
    | 'INVALIDCUSTOMAMOUNT';

/**
 * Interface for add to cart failure result.
 */
export interface IAddToCartFailureResult {
    failureReason: ICartActionFailureReason;

    stockLeft?: number;
    cartActionResult?: ICartActionResult;
    missingDimensions?: ProductDimension[];
}

/**
 * This setting defines the experience when a product is added to cart. Corresponds to the configuration in Fabrikam.
 */
export enum AddToCartBehavior {
    /**
     * Navigate to cart page.
     */
    goToCart = 'goToCart',

    /**
     * Show item added to cart popup.
     */
    showModal = 'showModal',

    /**
     * Show mini cart popup.
     */
    showMiniCart = 'showMiniCart',

    /**
     * Show item added to cart notification.
     */
    showNotification = 'showNotification',

    /**
     * Do nothing and stay on the page.
     */
    nothing = 'nothing'
}

const defaultQuantity = 1;

/**
 * The propagate result.
 * @param props -- The add to cart component props.
 * @param result -- The cart action result.
 */
const propagateResult = (props: IAddToCartComponentProps, result: ICartActionResult): void => {
    if (props.onAdd) {
        props.onAdd(result);
    }
};

/**
 * The propagate error.
 * @param props -- The add to cart component props.
 * @param result -- The add to cart failure result.
 */
const propagateError = (props: IAddToCartComponentProps, result: IAddToCartFailureResult): void => {
    if (props.onError) {
        props.onError(result);
    }
};

/**
 * Retrieves add to cart input from the component props.
 * @param props - Add to cart component props.
 * @returns Add to cart input.
 */
const getAddToCartInputFromProps = async (props: IAddToCartComponentProps) => {
    const products = props.orderDetailsProducts?.filter(product => !ObjectExtensions.isNullOrUndefined(product.simpleProduct));
    const input = products?.map(product => {
        return {
            product: product.simpleProduct!,
            count: ObjectExtensions.isNullOrUndefined(product.salesLine.Quantity) ? defaultQuantity : product.salesLine.Quantity,
            catalogId: product.salesLine.CatalogId,
            customPrice: props.orderDetailsActivePrices?.find(x => x.ProductId === product.simpleProduct?.RecordId)?.CustomerContextualPrice
        };
    });

    if (ArrayExtensions.hasElements(input)) {
        return input;
    }

    if (ArrayExtensions.hasElements(props.products)) {
        const actionContext = props.context.actionContext;
        const apiSettings = actionContext.requestContext.apiSettings;

        const searchCriteriaInput: ProductSearchCriteria = {
            Context: {
                ChannelId: apiSettings.channelId,
                CatalogId: props.catalogId ?? getCatalogId(props.context.actionContext.requestContext)
            },
            IncludeAttributes: false,
            Ids: props.products.map(product => product.productId)
        };

        const searchResult = await ProductsDataActions.searchByCriteriaAsync({ callerContext: actionContext }, searchCriteriaInput);
        const productSearchResultsWithImages = searchResult.map(productSearchResult => {
            const newProductSearchResult = { ...productSearchResult };
            const newImageUrl = generateProductImageUrl(newProductSearchResult, apiSettings);

            if (newImageUrl) {
                newProductSearchResult.PrimaryImageUrl = newImageUrl;
            }

            return newProductSearchResult;
        });

        return props.products.map(item => {
            const productData = productSearchResultsWithImages.find(product => product.RecordId === item.productId);
            let simpleProduct: SimpleProduct;
            if (productData) {
                simpleProduct = {
                    ...productData,
                    ProductTypeValue: ProductType.Variant,
                    AdjustedPrice: productData.Price,
                    BasePrice: productData.BasePrice ?? productData.Price
                };
            } else {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Cast to simple product since only record id is available.
                simpleProduct = {
                    RecordId: item.productId
                } as SimpleProduct;
            }

            return {
                product: simpleProduct,
                count: item.quantity,
                catalogId: item.catalogId
            };
        });
    }

    if (props.data?.product) {
        return [
            {
                product: props.data.product,
                count: props.quantity ?? defaultQuantity,
                customPrice: props.data?.productSearch?.Price,
                catalogId: props.catalogId
            }
        ];
    }

    return [];
};

/**
 * Interface for add to cart component.
 */
export interface IAddtoCartComponent extends IComponent<IAddToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddToCartComponentProps) => void;
}

/**
 * On click function.
 * @param _event - The mouse event.
 * @param props - The props.
 * @param setDisabled - Flag to define whether the element is disabled.
 * @param openModal - Flag to specify if it should open in a modal window.
 * @param setItemsAddedToCartDialogOpen - Sets items added to cart while dialog is open.
 * @param setErrorMessage - Error message.
 */
const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddToCartComponentProps,
    cartUrl: string | undefined,
    setWorking: (working: boolean) => void,
    setDisabled: (disabled: boolean) => void,
    setSuccess: (success: boolean) => void,
    openModal: (opened: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void
): Promise<void> => {
    if (!ArrayExtensions.hasElements(props.products)) {
        const cartError = addToCartError(props);

        if (cartError) {
            propagateError(props, cartError);
            return;
        }

        setDisabled(true);
    }

    setWorking(true);

    const work = async () => {
        const { product, productSearch } = props.data || {};

        if (!props.context || !product || !productSearch || !props.context.request.user.catalogId) {
            return;
        }

        const success = await CartHelper.addToCartAsync(
            props.context,
            productSearch,
            product,
            props.userHasAccess,
            props.quantity || 0,
            props.breakoff,
            props.onBuyValidate ? async () => props.onBuyValidate!(productSearch, props.quantity || 0) : undefined,
            props.nonBreakoffUOM
        );

        if (success) {
            props.onUpdateQuantity?.(0);
            propagateResult(props, { status: 'SUCCESS' });
            setSuccess(true);
        } else {
            propagateError(props, { failureReason: 'CARTACTIONFAILED' });
        }

        setDisabled(false);
    };

    if (props.cartQueue) {
        await props.cartQueue.add(work);
    } else {
        await work();
    }

    setWorking(false);
};

/**
 * Add to cart component action constant.
 */
const AddToCartComponentActions = {
    onClick
};

/**
 * Add to cart component.
 * @param props - The props.
 * @returns - The add to cart component.
 */
export const AddToCartFunctionalComponent: React.FC<IAddToCartComponentProps> = (props: IAddToCartComponentProps) => {
    const [success, setSuccess] = useState<Boolean>(false);
    const [working, setWorking] = useState<Boolean>(false);
    const [disabled, setDisabled] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isItemsAddedToCartDialogOpen, setItemsAddedToCartDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [addToCartInput, setAddToCartInput] = useState<{ product: SimpleProduct; count: number; }[]>([]);
    const cartUrl: string | undefined = useMemo(() => getUrlSync('cart', props.context.actionContext), []);

    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        await AddToCartComponentActions.onClick(event, props, cartUrl, setWorking, setDisabled, setSuccess, setModalOpen, setItemsAddedToCartDialogOpen, setErrorMessage);
    };

    /**
     * Close dialog.
     */
    const closeItemsAddedToCartDialog = React.useCallback(() => {
        setItemsAddedToCartDialogOpen(false);
    }, []);

    const priceComponent = props.data?.price ? (
        <PriceComponent
            data={{ price: props.data.price }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            freePriceText={props.dialogStrings?.freePriceText}
            originalPriceText={props.dialogStrings?.originalPriceText}
            currentPriceText={props.dialogStrings?.currentPriceText}
        />
    ) : (
        ''
    );

    const popupProps: IPopupProps = {
        context: props.context,
        className: 'msc-add-to-cart',
        id: props.id,
        typeName: props.typeName,
        data: {
            product: props.data?.product ? {
                ...props.data.product,
                DefaultUnitOfMeasure: humanizeUnitOfMeasure(props.context, props.data.product.DefaultUnitOfMeasure, props.data.product)
            } : undefined,
            price: props.data?.price
        },
        dialogStrings: props.dialogStrings,
        imageSettings: props.imageSettings,
        gridSettings: props.context.request.gridSettings,
        productQuantity: props.quantity !== undefined ? props.quantity : defaultQuantity,
        priceComponent,
        navigationUrl: props.navigationUrl,
        modalOpen,
        setModalOpen,
        telemetryContent: props.telemetryContent
    };

    const itemsAddedResource: IItemsAddedToCartResources = {
        viewCartButtonText: props.dialogStrings?.goToCartText ?? '',
        closeButtonLabel: props.dialogStrings?.closeNotificationLabel ?? 'Close',
        itemsAddedToCartHeaderText: props.dialogStrings?.headerMessageText ?? '',
        itemsAddedToCartFormatText: props.dialogStrings?.headerItemFormatText ?? '',
        linesAddedToCartFormatText: props.dialogStrings?.headerLinesFormatText ?? '{0}',
        itemsAddedValidationErrorMessage: errorMessage
    };

    const renderModalPopup = <Popup {...popupProps} />;
    let label = getLinkText(props);
    const payload = getPayloadObject(TelemetryEvent.AddToCart, props.telemetryContent!, label, '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payload);
    const buttonClassName = props.shouldSkipSiteSettings ? '' : 'msc-add-to-cart ';

    React.useEffect(() => {
        const retrieveAddToCartInput = async () => {
            const retrievedInput = await getAddToCartInputFromProps(props);
            retrievedInput.forEach(item => {
                item.product = {
                    ...item.product,
                    DefaultUnitOfMeasure: humanizeUnitOfMeasure(props.context, item.product.DefaultUnitOfMeasure, item.product)
                };
            });
            setAddToCartInput(retrievedInput);
        };

        void retrieveAddToCartInput();
    }, [props, setAddToCartInput]);

    React.useEffect(() => {
        if (success) {
            // Wait 2 seconds than set working to undefined
            const timeout = setTimeout(() => {
                setSuccess(false);
            }
            , 2000);
            return () => clearTimeout(timeout);
        } else {
            return undefined;
        }
    }, [success]);

    const product = props.data?.product;
    const lifeCycleStatus = product?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
    const isOutOfSeason = lifeCycleStatus === LifecycleStatus.OutOfSeason && !props.productAvailability?.AvailableQuantity;
    const isExpired = product?.Behavior?.IsSaleAtPhysicalStoresAllowed === false ||
        product?.Behavior?.IsSaleAtSelfCheckoutRegistersAllowed === false ||
        isOutOfSeason;

    const disableButton = !product || isExpired || props.disabled || disabled; // || isIntermediateState(props) <-- Removed cause it broke updating of state

    if (isExpired) {
        label = lifeCycleStatus === LifecycleStatus.OutOfSeason
            ? props.outOfSeasonBuyButtonText || 'Sesong'
            : props.expiredText || 'Utløpt';
    }

    return (
        <div>
            {renderModalPopup}
            <button
                className={classnames(buttonClassName, props.className, {
                    'loading': working // || isIntermediateState(props)
                    //'success': !working && success
                })}
                aria-label={props.addToCartArialLabel ?? label}
                title={props.addToCartArialLabel ?? label}
                {...attributes}
                onClick={onClickHandler}
                disabled={disableButton}
            >
                {/* {!working && success && <i className="fa-solid fa-check" />} */}
                {label}
            </button>
            <ItemsAddedToCartDialogComponent
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={itemsAddedResource}
                data={{
                    products: addToCartInput
                }}
                isModalOpen={isItemsAddedToCartDialogOpen}
                onClose={closeItemsAddedToCartDialog}
            />
        </div>
    );
};

// Set default props
AddToCartFunctionalComponent.defaultProps = {
    quantity: defaultQuantity
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns Gets the link text.
 */
const getLinkText = (props: IAddToCartComponentProps): string => {
    return props.addToCartText;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns The add to cart failure result.
 */
const addToCartError = (props: IAddToCartComponentProps): IAddToCartFailureResult | undefined => {
    const {
        data,
        //productAvailability,
        isCustomPriceSelected,
        customPriceAmount,
        maximumKeyInPrice,
        minimumKeyInPrice,
        defaultMaximumKeyInPrice = 100,
        defaultMinimumKeyInPrice = 10
    } = props;

    if (!data || !data.product.RecordId) {
        // No product exists, won't be able to add to cart
        return { failureReason: 'EMPTYINPUT' };
    }

    if (data.product.Dimensions) {
        const missingDimensions = data.product.Dimensions.filter(
            dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value)
        );

        if (ArrayExtensions.hasElements(missingDimensions)) {
            // At least one dimension with no value exists on the product, won't be able to add to cart
            return { failureReason: 'MISSINGDIMENSION', missingDimensions };
        }
    }

    // if (shouldShowOutOfStock(props, true)) {
    //     const defaultAvailableQuantity = 0; // Showing as out of stock if no available products found.
    //     const availableQuantity = productAvailability?.AvailableQuantity ?? defaultAvailableQuantity;
    //     const stockLeft = Math.max(availableQuantity, defaultAvailableQuantity);
    //
    //     return { failureReason: 'OUTOFSTOCK', stockLeft };
    // }

    // When Custom price is selected, if there is no keyed-in price or keyed-in price is out of limit, should return error.
    if (
        isCustomPriceSelected &&
        (!customPriceAmount ||
            customPriceAmount > (maximumKeyInPrice || defaultMaximumKeyInPrice) ||
            customPriceAmount < (minimumKeyInPrice || defaultMinimumKeyInPrice))
    ) {
        return { failureReason: 'INVALIDCUSTOMAMOUNT' };
    }

    // Only allow adding to cart if not showing out of stock
    return undefined;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @param includeCurrentQuantity - Flag to specify whether current quantity should be included.
 * @returns The dialog element.
 */
// const shouldShowOutOfStock = (props: IAddToCartComponentProps, includeCurrentQuantity: boolean): boolean => {
//     if (props.context.app.config.enableStockCheck === undefined || props.context.app.config.enableStockCheck === false) {
//         return false;
//     }
//
//     // When skip site settings do not need show out of stock on adding to cart
//     if (props.shouldSkipSiteSettings) {
//         return false;
//     }
//
//     if (
//         props.isLoading ||
//         props.isProductQuantityLoading ||
//         props.isUpdatingDimension ||
//         props.isLoadingDeliveryOptions ||
//         props.isUpdatingDeliveryOptions ||
//         props.isAddServiceItemToCart
//     ) {
//         // Out of stock turn off, don't bother showing out of stock
//         return false;
//     }
//
//     if (!props.data || !props.data.product.RecordId) {
//         // No product exists, don't bother showing out of stock
//         return false;
//     }
//
//     const hasAvailableProducts = props.hasAvailableProducts ?? true;
//     if (!hasAvailableProducts) {
//         return true;
//     }
//
//     if (props.data.product.Dimensions) {
//         if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
//             // At least one dimension with no value exists on the product, so also don't show out of stock
//             return false;
//         }
//     }
//
//     const includedQuantityNumber = includeCurrentQuantity && props.quantity ? props.quantity : defaultQuantity;
//
//     return !(
//         props.productAvailability &&
//         props.productAvailability.AvailableQuantity !== undefined &&
//         props.productAvailability.AvailableQuantity >= includedQuantityNumber
//     );
// };

// @ts-expect-error
const isIntermediateState = (props: IAddToCartComponentProps): boolean => {
    if (props.data?.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also not in intermediate state
            return false;
        }
    }

    if (!props.isLoading && !props.isUpdatingDimension && !props.isLoadingDeliveryOptions && !props.isUpdatingDeliveryOptions) {
        return false;
    }

    return true;
};
// @ts-ignore
export const AddToCartComponent: React.FunctionComponent<IAddToCartComponentProps> = msdyn365Commerce.createComponent<IAddtoCartComponent>(
    'AddToCart',
    { component: AddToCartFunctionalComponent, ...AddToCartComponentActions }
);
